import {
  StockCommandEnvelopeV1Schema,
  StockCommandV1Schema,
  materializeStockCommand,
  type StockCommandEnvelopeV1,
  type StockCommandV1,
  type SyncEventInput,
} from '@trackeat/shared';
import { getMeta } from '../db/local.js';
import {
  enqueueAtomicRpcV1,
  type AtomicRpcHandle,
} from '../sync/atomicRpcOutbox.js';
import { stockCommandDigest } from '../sync/stockCommandOutbox.js';

export const STOCK_RECONCILIATION_SCHEMA =
  'trackeat.stock-reconciliation' as const;
export const STOCK_RECONCILIATION_RESULT_SCHEMA =
  'trackeat.stock-reconciliation-result' as const;
export const STOCK_RECONCILIATION_MARKER_PREFIX =
  'stock-reconciliation:v1:' as const;

export type StockReconciliationModeV1 =
  | 'physical_recount'
  | 'product_reassignment';

export interface StockReconciliationSnapshotRowV1 {
  batch_lot: string | null;
  expiration_date: string | null;
  supplier_id: string | null;
  document_id: string | null;
  storage_unit_id: string | null;
  qty: number;
  uom: string;
  value: number | null;
  currency: string | null;
}

export interface StockReconciliationRequestV1
  extends Record<string, unknown> {
  schema: typeof STOCK_RECONCILIATION_SCHEMA;
  version: 1;
  request_id: string;
  mode: StockReconciliationModeV1;
  tenant_id: string;
  business_id: string;
  source_food_id: string;
  target_food_id: string;
  source_food_version: number;
  target_food_version: number;
  source_uom: string;
  target_uom: string;
  requested_at: string;
  operator_name: string;
  archive_source: boolean;
  source_recipe_count: number;
  source_snapshot: StockReconciliationSnapshotRowV1[];
  marker_command: StockCommandEnvelopeV1;
  snapshot_command: StockCommandEnvelopeV1 | null;
  register_command: StockCommandEnvelopeV1 | null;
}

export interface BuildStockReconciliationInputV1 {
  mode: StockReconciliationModeV1;
  requestId: string;
  tenantId: string;
  businessId: string;
  sourceFoodId: string;
  targetFoodId: string;
  sourceFoodVersion: number;
  targetFoodVersion: number;
  sourceUom: string;
  targetUom: string;
  requestedAt: string;
  operatorName: string;
  archiveSource: boolean;
  sourceRecipeCount: number;
  rows: readonly StockReconciliationSnapshotRowV1[];
  deviceId: string;
}

function cleanUom(value: string): string {
  const clean = value.trim().toLowerCase();
  if (!clean || clean.length > 10) {
    throw new Error('stock_reconciliation_invalid_uom');
  }
  return clean;
}

function validateRows(
  rows: readonly StockReconciliationSnapshotRowV1[],
  targetUom: string,
): StockReconciliationSnapshotRowV1[] {
  const seen = new Set<string>();
  return rows.map((row) => {
    const lot = row.batch_lot?.trim() || null;
    const storageUnitId = row.storage_unit_id?.trim() || null;
    const key = JSON.stringify([lot, storageUnitId]);
    if (seen.has(key)) {
      throw new Error('stock_reconciliation_duplicate_locator');
    }
    seen.add(key);
    if (!Number.isFinite(row.qty) || row.qty < 0) {
      throw new Error('stock_reconciliation_invalid_quantity');
    }
    if (
      row.value !== null
      && (!Number.isFinite(row.value) || row.value < 0)
    ) {
      throw new Error('stock_reconciliation_invalid_value');
    }
    if (
      (row.value === null) !== (row.currency === null)
      || (
        row.currency !== null
        && !/^[A-Z]{3}$/.test(row.currency)
      )
    ) {
      throw new Error('stock_reconciliation_invalid_currency');
    }
    if (cleanUom(row.uom) !== targetUom) {
      throw new Error('stock_reconciliation_mixed_uom');
    }
    return {
      ...row,
      batch_lot: lot,
      storage_unit_id: storageUnitId,
      qty: row.qty,
      uom: targetUom,
    };
  });
}

async function envelopeFor(
  commandInput: StockCommandV1,
  deviceId: string,
  requestedAt: string,
): Promise<StockCommandEnvelopeV1> {
  const command = StockCommandV1Schema.parse(commandInput);
  const events: SyncEventInput[] = materializeStockCommand(command).map(
    (draft) => ({
      client_uuid: crypto.randomUUID(),
      tenant_id: draft.tenant_id,
      business_id: draft.business_id,
      device_id: deviceId,
      client_ts: requestedAt,
      kind: draft.kind,
      payload: draft.payload,
    }),
  );
  return StockCommandEnvelopeV1Schema.parse({
    schema: 'trackeat.stock-command',
    version: 1,
    command,
    command_digest: await stockCommandDigest(command),
    device_id: deviceId,
    events,
    master_effects: [],
  });
}

export async function buildStockReconciliationRequestV1(
  input: BuildStockReconciliationInputV1,
): Promise<StockReconciliationRequestV1> {
  const sourceUom = cleanUom(input.sourceUom);
  const targetUom = cleanUom(input.targetUom);
  if (
    !Number.isSafeInteger(input.sourceFoodVersion)
    || input.sourceFoodVersion < 0
    || !Number.isSafeInteger(input.targetFoodVersion)
    || input.targetFoodVersion < 0
    || !Number.isSafeInteger(input.sourceRecipeCount)
    || input.sourceRecipeCount < 0
  ) {
    throw new Error('stock_reconciliation_invalid_version');
  }
  if (
    input.mode === 'product_reassignment'
    && input.sourceFoodId === input.targetFoodId
  ) {
    throw new Error('stock_reconciliation_target_matches_source');
  }
  if (
    input.mode === 'physical_recount'
    && input.sourceFoodId !== input.targetFoodId
  ) {
    throw new Error('stock_reconciliation_recount_target_mismatch');
  }
  const rows = validateRows(input.rows, targetUom);
  const markerCommand = await envelopeFor({
    version: 1,
    command_id: crypto.randomUUID(),
    tenant_id: input.tenantId,
    movement_class: 'lot_state',
    economic_effect: 'stock_only',
    business_id: input.businessId,
    reason: `${STOCK_RECONCILIATION_MARKER_PREFIX}${input.requestId}`,
    lines: [{
      food_id: input.sourceFoodId,
      batch_lot: null,
      expiration_date: null,
      supplier_id: null,
      document_id: null,
      storage_unit_id: null,
      operator_name: input.operatorName,
      line_ref: `${STOCK_RECONCILIATION_MARKER_PREFIX}${input.requestId}`,
      action: 'update_metadata',
      uom: sourceUom,
    }],
  }, input.deviceId, input.requestedAt);

  const positiveRows = rows.filter((row) => row.qty > 0);
  const snapshotCommand = positiveRows.length === 0
    ? null
    : await envelopeFor({
      version: 1,
      command_id: crypto.randomUUID(),
      tenant_id: input.tenantId,
      movement_class: 'receive',
      economic_effect: 'stock_only',
      business_id: input.businessId,
      reason: `${STOCK_RECONCILIATION_MARKER_PREFIX}${input.requestId}:snapshot`,
      lines: positiveRows.map((row, index) => ({
        food_id: input.targetFoodId,
        batch_lot: row.batch_lot,
        expiration_date: row.expiration_date,
        supplier_id: row.supplier_id,
        document_id: row.document_id,
        storage_unit_id: row.storage_unit_id,
        operator_name: input.operatorName,
        line_ref: `${STOCK_RECONCILIATION_MARKER_PREFIX}${input.requestId}:snapshot:${index + 1}`,
        lot_action: 'updated_active' as const,
        source_qty: row.qty,
        source_uom: targetUom,
        source_unit_price: null,
        source_line_total: row.value,
        currency: row.currency ?? 'EUR',
        stock_uom: targetUom,
        conversion: { factor: 1, source: 'identity' as const },
      })),
    }, input.deviceId, input.requestedAt);
  const rowsWithLot = positiveRows.filter((row) => row.batch_lot !== null);
  const registerCommand = rowsWithLot.length === 0
    ? null
    : await envelopeFor({
      version: 1,
      command_id: crypto.randomUUID(),
      tenant_id: input.tenantId,
      movement_class: 'lot_state',
      economic_effect: 'stock_only',
      business_id: input.businessId,
      reason: `${STOCK_RECONCILIATION_MARKER_PREFIX}${input.requestId}:register`,
      lines: rowsWithLot.map((row, index) => ({
        food_id: input.targetFoodId,
        batch_lot: row.batch_lot,
        expiration_date: row.expiration_date,
        supplier_id: row.supplier_id,
        document_id: row.document_id,
        storage_unit_id: row.storage_unit_id,
        operator_name: input.operatorName,
        line_ref: `${STOCK_RECONCILIATION_MARKER_PREFIX}${input.requestId}:register:${index + 1}`,
        action: 'register' as const,
        uom: targetUom,
      })),
    }, input.deviceId, input.requestedAt);

  return {
    schema: STOCK_RECONCILIATION_SCHEMA,
    version: 1,
    request_id: input.requestId,
    mode: input.mode,
    tenant_id: input.tenantId,
    business_id: input.businessId,
    source_food_id: input.sourceFoodId,
    target_food_id: input.targetFoodId,
    source_food_version: input.sourceFoodVersion,
    target_food_version: input.targetFoodVersion,
    source_uom: sourceUom,
    target_uom: targetUom,
    requested_at: input.requestedAt,
    operator_name: input.operatorName.trim(),
    archive_source: input.archiveSource,
    source_recipe_count: input.sourceRecipeCount,
    source_snapshot: rows,
    marker_command: markerCommand,
    snapshot_command: snapshotCommand,
    register_command: registerCommand,
  };
}

export async function queueStockReconciliationV1(
  input: Omit<BuildStockReconciliationInputV1, 'deviceId'>,
): Promise<AtomicRpcHandle> {
  const deviceId = (await getMeta('device_id')) ?? 'unknown';
  const request = await buildStockReconciliationRequestV1({
    ...input,
    deviceId,
  });
  const queued = await enqueueAtomicRpcV1({
    kind: 'stock_reconciliation_v1',
    tenantId: input.tenantId,
    businessId: input.businessId,
    scopeKey: `stock-reconciliation:${input.businessId}:${input.sourceFoodId}`,
    request,
    optimisticMasters: [],
    now: input.requestedAt,
  });
  return queued.handle;
}

export function parseStockReconciliationResultV1(
  value: unknown,
  request: StockReconciliationRequestV1,
  requestDigest: string,
): void {
  if (!value || typeof value !== 'object') {
    throw new Error('stock_reconciliation_invalid_receipt');
  }
  const result = value as Record<string, unknown>;
  if (
    result.schema !== STOCK_RECONCILIATION_RESULT_SCHEMA
    || result.version !== 1
    || result.request_id !== request.request_id
    || result.request_digest !== requestDigest
    || result.tenant_id !== request.tenant_id
    || result.business_id !== request.business_id
    || result.source_food_id !== request.source_food_id
    || result.target_food_id !== request.target_food_id
    || typeof result.replayed !== 'boolean'
  ) {
    throw new Error('stock_reconciliation_receipt_mismatch');
  }
}
